import React, { useRef, useState } from "react";
import api from "@/lib/api";
import { useApp, DEFAULT_DICT } from "@/contexts/AppContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Camera, RotateCcw } from "lucide-react";
import { toast } from "sonner";

const IMAGES = [["hero", "Gambar Utama Beranda"], ["about_hero", "Gambar Halaman Tentang"], ["booking_banner", "Banner Halaman Booking"], ["logo", "Logo"]];
const GROUPS = [
  ["Beranda", ["hero_title", "hero_sub", "tagline", "nusuk_registered", "exp_driver", "on_time", "clean_fleet", "our_fleet", "fleet_sub", "packages_title", "packages_sub", "testimonials", "testimonials_sub", "svc_airport_pickup", "svc_city_tour", "svc_intercity"]],
  ["Booking & Invoice", ["book_now", "booking_intro", "dp_note", "confirm_booking", "contact_details", "pickup_location_ph", "transfer_to", "remaining_on_trip", "proof_desc"]],
  ["Tentang & Layanan Lainnya", ["about_us", "legality_title", "gallery_title", "other_services_title", "other_services_sub", "osvc_konsultasi_desc", "osvc_visa_desc", "osvc_hotel_desc", "osvc_muthawwif_desc", "osvc_dokumentasi_desc", "osvc_la_umroh_desc", "osvc_cta_title"]],
  ["WhatsApp", ["need_help", "wa_help_msg", "share_fleet_msg", "share_testi_msg", "osvc_wa_msg"]],
];

function ImageSlot({ k, label, url, onUploaded }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return; setBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post(`/site-images/${k}`, fd); onUploaded(k, data.url); toast.success("Tersimpan"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border overflow-hidden" data-testid={`img-slot-${k}`}>
      <div className="h-32 bg-slate-100 grid place-items-center">{url ? <img src={url} alt={label} className="w-full h-full object-cover" /> : <span className="text-xs text-slate-400">Default</span>}</div>
      <div className="p-2 flex items-center justify-between gap-2"><span className="text-xs font-medium">{label}</span>
        <Button size="sm" variant="outline" disabled={busy} onClick={() => ref.current.click()} data-testid={`img-btn-${k}`}><Camera className="w-3 h-3 mr-1" />{busy ? "..." : "Ganti"}</Button>
        <input ref={ref} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => { upload(e.target.files[0]); e.target.value = ""; }} data-testid={`img-input-${k}`} /></div>
    </div>
  );
}

export default function ContentAdmin({ s, setS }) {
  const { lang } = useApp();
  const [editLang, setEditLang] = useState(lang);
  const [q, setQ] = useState("");
  const content = s.content || { ID: {}, EN: {} };
  const cur = content[editLang] || {};
  const setText = (k, v) => setS({ ...s, content: { ...content, [editLang]: { ...cur, [k]: v } } });
  const reset = (k) => { const n = { ...cur }; delete n[k]; setS({ ...s, content: { ...content, [editLang]: n } }); };
  return (
    <div className="space-y-4">
      <Card><CardHeader><CardTitle className="text-base">Gambar Situs</CardTitle></CardHeader>
        <CardContent className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {IMAGES.map(([k, l]) => <ImageSlot key={k} k={k} label={l} url={s.images?.[k]} onUploaded={(key, url) => setS({ ...s, images: { ...(s.images || {}), [key]: url } })} />)}
        </CardContent></Card>
      <div className="flex flex-wrap items-center gap-3">
        <div className="inline-flex rounded-full bg-slate-100 p-1">{["ID", "EN"].map((l) => <button key={l} onClick={() => setEditLang(l)} data-testid={`content-lang-${l}`} className={`px-4 py-1 text-sm rounded-full ${editLang === l ? "bg-emerald-600 text-white" : "text-slate-600"}`}>{l}</button>)}</div>
        <Input className="h-9 max-w-xs" placeholder="Cari teks..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="content-search" />
        <span className="text-xs text-slate-500">Kosongkan / reset untuk memakai teks bawaan. Klik Simpan di atas.</span>
      </div>
      {GROUPS.map(([g, keys]) => {
        const shown = keys.filter((k) => !q || k.includes(q.toLowerCase()) || (DEFAULT_DICT[editLang][k] || "").toLowerCase().includes(q.toLowerCase()) || (cur[k] || "").toLowerCase().includes(q.toLowerCase()));
        if (!shown.length) return null;
        return (
          <Card key={g}><CardHeader><CardTitle className="text-base">{g}</CardTitle></CardHeader>
            <CardContent className="grid md:grid-cols-2 gap-3">
              {shown.map((k) => { const def = DEFAULT_DICT[editLang][k] || ""; const long = def.length > 60;
                return (
                  <div key={k} data-testid={`content-field-${k}`}>
                    <div className="flex items-center justify-between"><Label className="text-xs font-mono text-slate-500">{k}</Label>
                      {cur[k] != null && <button type="button" onClick={() => reset(k)} className="text-xs text-slate-500 hover:text-rose-600 flex items-center gap-1" data-testid={`content-reset-${k}`}><RotateCcw className="w-3 h-3" />reset</button>}</div>
                    {long ? <Textarea rows={3} value={cur[k] ?? def} onChange={(e) => setText(k, e.target.value)} data-testid={`content-input-${k}`} />
                      : <Input value={cur[k] ?? def} onChange={(e) => setText(k, e.target.value)} data-testid={`content-input-${k}`} />}
                  </div>);
              })}
            </CardContent></Card>);
      })}
    </div>
  );
}
